import { Component, computed, effect, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';
import { CommonService, ImageViewerAnimation } from '@shared/services/Common/common.service';
import { Dialog } from '@angular/cdk/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { downloadBlob } from '@utils/download.util';
import { DialogShellComponent } from '@shared/components/dialog-shell/dialog-shell.component';

@Component({
  selector: 'app-image-viewer',
  templateUrl: './image-viewer.component.html',
  imports: [TranslatePipe, DialogShellComponent],
})
export class ImageViewerComponent {
  common = inject(CommonService);
  private dialog = inject(Dialog);
  private translate = inject(TranslateService);

  /** Contenedor del visor (envuelve el shell): recibe el foco al abrirse y lo mantiene (focus trap). */
  private viewerContent = viewChild<ElementRef<HTMLElement>>('viewerContent');

  lines = computed(() => this.common.imageViewerSignal()?.lines ?? []);
  lineIndex = computed(() => this.common.imageViewerSignal()?.lineIndex ?? 0);
  images = computed(() => this.lines()[this.lineIndex()]?.images ?? []);
  index = computed(() => this.common.imageViewerSignal()?.index ?? 0);
  animations = computed(() => this.lines()[this.lineIndex()]?.animations ?? []);

  /** Opciones de chips para elegir la línea del ejercicio (Original + Alternativa N). */
  lineOptions = computed(() => {
    const lines = this.lines();
    if (lines.length <= 1) return [];
    return lines.map((_, li) => ({
      index: li,
      label: li === 0 ? this.translate.instant('GRAPHIC_BRANCH_ORIGINAL') : this.translate.instant('GRAPHIC_BRANCH') + ' ' + li,
    }));
  });

  /** Elige la línea del ejercicio (0 = Original, 1..N = Alternativa N). */
  selectLine(lineIndex: number): void {
    if (lineIndex === this.lineIndex()) return;
    this.imageLoaded.set(false);
    this.common.setImageViewerLine(lineIndex);
  }

  /** Indica que la imagen actual terminó de cargar (controla el fundido de entrada). */
  imageLoaded = signal(false);
  /** Indica que se está generando el ZIP de descarga masiva. */
  downloadingAll = signal(false);

  /** Posición inicial del gesto táctil (swipe). */
  private touchStartX = 0;
  private touchStartY = 0;
  /** Marca que acaba de ocurrir un swipe para no cerrar el visor con el click sintético. */
  private swiped = false;

  constructor() {
    // Foco al abrir el visor: se espera al microtask para que el DOM ya esté renderizado.
    effect(() => {
      if (this.common.imageViewerSignal()) {
        queueMicrotask(() => this.viewerContent()?.nativeElement.focus());
      }
    });

    // Al abrir el visor, respetar la línea preseleccionada (la pestaña activa
    // del detalle) y clampar el índice al rango de esa línea. No se resetea
    // imageLoaded: si el src final ya era el correcto no vuelve a disparar
    // load y la imagen quedaría invisible.
    effect(() => {
      const data = this.common.imageViewerSignal();
      if (!data) return;
      const lineIndex = data.lineIndex ?? 0;
      const images = data.lines[lineIndex]?.images ?? [];
      if ((data.index ?? 0) >= images.length) {
        this.common.setImageViewerLine(lineIndex);
      }
    });

    // Precarga de imágenes adyacentes para que la navegación sea instantánea.
    effect(() => {
      const imgs = this.images();
      const current = this.index();
      if (imgs.length > 1) this.preloadAdjacent(imgs, current);
    });
  }

  get currentImage(): string {
    return this.images()[this.index()] || '';
  }

  /** Animación asociada a la imagen actual (null si no tiene). */
  get currentAnimation(): ImageViewerAnimation | null {
    return this.animations()[this.index()] ?? null;
  }

  /** Nombre del ejercicio/jugada para nombrar los archivos descargados. */
  get name(): string | undefined {
    return this.common.imageViewerSignal()?.name;
  }

  close(): void {
    this.common.closeImageViewer();
  }

  /** Navega a un índice con wrap-around (negativos incluidos). */
  goTo(newIndex: number): void {
    const imgs = this.images();
    if (imgs.length === 0) return;
    const current = this.index();
    const wrapped = ((newIndex % imgs.length) + imgs.length) % imgs.length;
    if (wrapped !== current) this.imageLoaded.set(false);
    this.common.goToImageViewer(wrapped);
  }

  onImageLoad(): void {
    this.imageLoaded.set(true);
  }

  onBackdropClick(): void {
    if (this.swiped) {
      // El click sintético tras un swipe no debe cerrar el visor.
      this.swiped = false;
      return;
    }
    this.close();
  }

  onTouchStart(event: TouchEvent): void {
    const touch = event.touches[0];
    if (touch) {
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    }
  }

  onTouchEnd(event: TouchEvent): void {
    const touch = event.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.swiped = true;
      this.goTo(this.index() + (deltaX < 0 ? 1 : -1));
    }
  }

  /**
   * Teclado: Escape cierra, flechas navegan y Tab queda atrapado dentro del visor.
   * Solo actúa cuando el visor está abierto.
   */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.common.imageViewerSignal()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.index() - 1);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.index() + 1);
      return;
    }
    if (event.key === 'Tab') this.trapTab(event);
  }

  /** Mantiene el foco dentro del visor al pulsar Tab (focus trap manual). */
  private trapTab(event: KeyboardEvent): void {
    const container = this.viewerContent()?.nativeElement;
    if (!container) return;
    const buttons = container.querySelectorAll<HTMLButtonElement>('button');
    if (buttons.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }
    const first: HTMLButtonElement | null = buttons.item(0);
    const last: HTMLButtonElement | null = buttons.item(buttons.length - 1);
    if (!first || !last) return;
    const active = document.activeElement;
    const outside = !container.contains(active);
    if (event.shiftKey && (outside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (outside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Abre el diálogo que reproduce la animación del gráfico actual.
   * El diálogo (y Konva) se cargan bajo demanda para no inflar el bundle inicial.
   */
  async viewAnimation(): Promise<void> {
    const anim = this.currentAnimation;
    if (!anim) return;
    const { BoardPreviewDialogComponent } = await import('@board/components/board-preview-dialog/board-preview-dialog.component');
    this.dialog.open(BoardPreviewDialogComponent, {
      disableClose: false,
      data: { json: anim.json, courtMode: anim.courtMode, name: this.name, graphicNumber: this.index() + 1 },
    });
  }

  /**
   * Descarga la imagen actual con su nombre correcto.
   * El fetch+blob evita que el atributo `download` se ignore en URLs cross-origin.
   */
  async downloadImage(): Promise<void> {
    const url = this.currentImage;
    if (!url) return;
    const fileName = this.buildFileName(this.index() + 1);
    try {
      const blob = await this.fetchAsBlob(url);
      downloadBlob(blob, fileName);
    } catch {
      // Fallback: descarga directa por URL (el nombre puede no respetarse en cross-origin).
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  /** Descarga todas las imágenes en un único ZIP. */
  async downloadAllImages(): Promise<void> {
    const imgs = this.images();
    if (imgs.length === 0 || this.downloadingAll()) return;
    this.downloadingAll.set(true);
    try {
      const { zipSync } = await import('fflate');
      const entries: Record<string, Uint8Array> = {};
      const results = await Promise.allSettled(
        imgs.map(async (url) => {
          const blob = await this.fetchAsBlob(url);
          return new Uint8Array(await blob.arrayBuffer());
        }),
      );
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          entries[this.buildFileName(i + 1)] = result.value;
        }
      });
      if (Object.keys(entries).length === 0) throw new Error('No se pudo obtener ninguna imagen.');
      const zipped = zipSync(entries, { level: 0 });
      downloadBlob(new Blob([zipped], { type: 'application/zip' }), `${this.name ?? 'graficos'}.zip`);
    } catch {
      this.common.showAlert('error', this.translate.instant('IMAGE_VIEWER_DOWNLOAD_ALL_ERROR'));
    } finally {
      this.downloadingAll.set(false);
    }
  }

  /** Obtiene una imagen remota como Blob. */
  private async fetchAsBlob(url: string): Promise<Blob> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  }

  /** Nombre de archivo para la imagen `position` (1-based): "Nombre-N.png". */
  private buildFileName(position: number): string {
    if (this.name) return `${this.name}-${position}.png`;
    const base = this.images()[position - 1]?.split('/').pop()?.split('?')[0] || 'imagen';
    return base.toLowerCase().endsWith('.png') ? base : `${base}.png`;
  }

  /** Precarga en memoria las imágenes anterior y siguiente a la actual. */
  private preloadAdjacent(images: string[], current: number): void {
    const next = images[(current + 1) % images.length];
    const prev = images[(current - 1 + images.length) % images.length];
    [prev, next].forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }
}
